import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { MdContentCopy, MdLink, MdRefresh } from "react-icons/md";
import Loader from "../../components/Loader";
import NoData from "../../components/NoData";
import { AuthContext } from "../../context/AuthContext";
import ShareLinkServices from "../../services/ShareLinkServices";
import "../../styles/shareLink.css";

const roleLabel = (roleId, roleName) => {
  if (roleName) return roleName;
  if (roleId === 1) return "Super Admin";
  if (roleId === 2) return "Admin";
  if (roleId === 5) return "Sub Admin";
  if (roleId === 3) return "Outlet";
  return `Role ${roleId}`;
};

/**
 * Links come back relative when the server has no PUBLIC_SHARE_BASE_URL
 * (the live host has none). A relative link pasted into WhatsApp goes
 * nowhere, so it is completed with the API host this page already talks
 * to - which is where /api/get-app and the APK download are served.
 */
const API_BASE = String(process.env.REACT_APP_NODE_URL || "").replace(/\/+$/, "");
const absUrl = (u) => {
  const s = String(u || "");
  return s.startsWith("/") ? `${API_BASE || window.location.origin}${s}` : s;
};

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch (_) {
    return String(value);
  }
};

const ShareLinkManage = () => {
  const { user } = useContext(AuthContext);
  const shareApi = useMemo(() => new ShareLinkServices(), []);
  const isSuperOrAdmin = user?.role_id === 1 || (user?.role_id === 2 || user?.role_id === 5);
  const isSuperAdmin = user?.role_id === 1;

  const [loading, setLoading] = useState(false);
  const [copying, setCopying] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [latestUrl, setLatestUrl] = useState("");
  const [latestMeta, setLatestMeta] = useState(null);
  const [links, setLinks] = useState([]);
  const [apkInfo, setApkInfo] = useState(null);
  const [apkVersion, setApkVersion] = useState("1.0.0");
  const [uploadingApk, setUploadingApk] = useState(false);

  const loadCandidates = useCallback(async () => {
    if (!isSuperOrAdmin) return;
    try {
      const res = await shareApi.getShareCandidates();
      if (res?.isSuccess) {
        const rows = res.data || [];
        setCandidates(rows);
        // An Admin defaults to their own link; the Super Admin is never in
        // the list and must pick who the link is for.
        if (!selectedUserId && rows.length) {
          const me = rows.find((r) => Number(r.id) === Number(user?.user_id));
          if (me) setSelectedUserId(String(me.id));
        }
      }
    } catch (_) {
      // ignore - page still works for self-copy
    }
  }, [isSuperOrAdmin, shareApi, selectedUserId, user]);

  const loadLinks = useCallback(async () => {
    setLoading(true);
    try {
      const res =
        user?.role_id === 1 || (user?.role_id === 2 || user?.role_id === 5)
          ? await shareApi.getAllLinks()
          : await shareApi.getMyLinks();
      if (res?.isSuccess) {
        setLinks((res.data || []).map((r) => ({ ...r, url: absUrl(r.url) })));
      } else {
        toast.error(res?.message || "Failed to load links");
      }
    } catch (_) {
      toast.error("Failed to load share links");
    } finally {
      setLoading(false);
    }
  }, [shareApi, user]);

  const loadApkInfo = useCallback(async () => {
    try {
      const res = await shareApi.getApkInfo();
      if (res?.isSuccess) {
        setApkInfo(
          res.data
            ? { ...res.data, download_url: absUrl(res.data.download_url) }
            : res.data,
        );
      }
    } catch (_) {}
  }, [shareApi]);

  useEffect(() => {
    loadCandidates();
    loadLinks();
    loadApkInfo();
  }, [loadCandidates, loadLinks, loadApkInfo]);

  const copyText = async (text) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const el = document.createElement("textarea");
        el.value = text;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
      toast.success("Link copied");
    } catch (_) {
      toast.error("Could not copy. Select and copy manually.");
    }
  };

  const handleGenerateAndCopy = async () => {
    setCopying(true);
    try {
      const payload = {};
      if (isSuperOrAdmin && selectedUserId) {
        payload.for_user_id = Number(selectedUserId);
      }
      const res = await shareApi.generate(payload);
      if (!res?.isSuccess || !res?.data?.url) {
        toast.error(res?.message || "Failed to generate link");
        return;
      }
      const url = absUrl(res.data.url);
      setLatestUrl(url);
      setLatestMeta({ ...res.data, url });
      await copyText(url);
      loadLinks();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to generate link");
    } finally {
      setCopying(false);
    }
  };

  const handleDeactivate = async (id) => {
    try {
      const res = await shareApi.deactivate(id);
      if (res?.isSuccess) {
        toast.success(res.message || "Link deactivated");
        loadLinks();
      } else {
        toast.error(res?.message || "Failed to deactivate");
      }
    } catch (_) {
      toast.error("Failed to deactivate link");
    }
  };

  const bumpCopyCount = (id, nextCount) => {
    const linkId = Number(id);
    setLinks((prev) =>
      prev.map((item) =>
        Number(item.id) === linkId
          ? {
              ...item,
              copy_count:
                typeof nextCount === "number"
                  ? nextCount
                  : Number(item.copy_count || 0) + 1,
            }
          : item,
      ),
    );
  };

  const handleCopySavedLink = async (row) => {
    if (!row?.url || !row?.id) return;
    await copyText(row.url);
    // Optimistic UI so Copies updates immediately.
    bumpCopyCount(row.id);
    setLatestMeta((prev) =>
      Number(prev?.id) === Number(row.id)
        ? { ...prev, copy_count: Number(prev.copy_count || 0) + 1 }
        : prev,
    );
    try {
      const res = await shareApi.recordCopy(Number(row.id));
      if (res?.isSuccess && res.data?.copy_count != null) {
        const next = Number(res.data.copy_count);
        bumpCopyCount(row.id, next);
        setLatestMeta((prev) =>
          Number(prev?.id) === Number(row.id)
            ? { ...prev, copy_count: next }
            : prev,
        );
      } else {
        toast.error(res?.message || "Could not save copy count");
        loadLinks();
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          "Copy saved to clipboard, but count was not updated on server",
      );
      loadLinks();
    }
  };

  const shortUrl = (url) => {
    const s = String(url || "");
    if (s.length <= 42) return s;
    return `${s.slice(0, 28)}…${s.slice(-10)}`;
  };

  const handleApkUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!String(file.name || "").toLowerCase().endsWith(".apk")) {
      toast.error("Please select a .apk file");
      return;
    }
    setUploadingApk(true);
    try {
      const res = await shareApi.uploadApk(file, apkVersion);
      if (res?.isSuccess) {
        toast.success(res.message || "APK uploaded");
        setApkInfo(res.data);
        loadApkInfo();
      } else {
        toast.error(res?.message || "Upload failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "APK upload failed");
    } finally {
      setUploadingApk(false);
    }
  };

  return (
    <div className="share-link-page">
      {isSuperAdmin && (
        <div className="share-link-card">
          <h2>APK Upload</h2>
          <p>
            Upload the release APK. Shared links open a download page on the API
            server (LAN IP or ngrok).
          </p>
          <div className="share-link-form">
            <div className="share-field">
              <label>Version label</label>
              <input
                type="text"
                value={apkVersion}
                onChange={(e) => setApkVersion(e.target.value)}
                placeholder="1.0.0"
                style={{
                  width: "100%",
                  padding: "11px 12px",
                  borderRadius: 10,
                  border: "1px solid #d1d5db",
                }}
              />
            </div>
            <label className="share-copy-btn" style={{ cursor: "pointer" }}>
              {uploadingApk ? "Uploading..." : "Upload APK"}
              <input
                type="file"
                accept=".apk,application/vnd.android.package-archive"
                hidden
                disabled={uploadingApk}
                onChange={handleApkUpload}
              />
            </label>
          </div>
          <div className="share-latest" style={{ marginTop: 12 }}>
            <small>
              Status:{" "}
              {apkInfo?.available
                ? `Ready (${apkInfo.size_mb || "?"} MB)`
                : "Not uploaded yet"}
              {apkInfo?.download_url ? (
                <>
                  {" · "}
                  <button
                    type="button"
                    className="share-inline-copy"
                    onClick={() => copyText(apkInfo.download_url)}
                  >
                    Copy download URL
                  </button>
                </>
              ) : null}
            </small>
          </div>
        </div>
      )}

      <div className="share-link-card">
        <div className="share-link-header">
          <div>
            <h2>
              <MdLink style={{ marginRight: 8, verticalAlign: "middle" }} />
              App Share Link (APK install)
            </h2>
            <p>
              Select an admin/outlet, then copy the invite link. Friend opens it on
              phone, downloads APK, installs, and registers only inside the app.
              Valid 30 days.
            </p>
          </div>
          <button
            type="button"
            className="share-refresh-btn"
            onClick={() => {
              loadLinks();
              loadApkInfo();
            }}
            title="Refresh"
          >
            <MdRefresh size={18} />
          </button>
        </div>

        <div className="share-link-form">
          {isSuperOrAdmin && (
            <div className="share-field">
              <label>Select Admin / Outlet</label>
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
              >
                <option value="">Select user</option>
                {candidates.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.username}) — {roleLabel(c.role_id, c.role_name)}
                  </option>
                ))}
              </select>
            </div>
          )}

          <button
            type="button"
            className="share-copy-btn"
            onClick={handleGenerateAndCopy}
            disabled={copying || (isSuperOrAdmin && !selectedUserId)}
          >
            <MdContentCopy size={18} />
            {copying ? "Generating..." : "Generate & Copy Link"}
          </button>
        </div>

        {latestUrl && (
          <div className="share-latest">
            <label>Latest link</label>
            <div className="share-latest-row">
              <input type="text" readOnly value={latestUrl} />
              <button
                type="button"
                onClick={() => {
                  if (latestMeta?.id) {
                    handleCopySavedLink({
                      id: latestMeta.id,
                      url: latestUrl,
                      copy_count: latestMeta.copy_count,
                    });
                  } else {
                    copyText(latestUrl);
                  }
                }}
              >
                Copy
              </button>
            </div>
            {latestMeta?.expires_at && (
              <small>
                Expires: {formatDate(latestMeta.expires_at)}
                {latestMeta.reused ? " (reused existing link)" : " (new link)"}
              </small>
            )}
          </div>
        )}
      </div>

      <div className="share-link-card">
        <div className="share-saved-header">
          <div>
            <h3>Saved Links</h3>
            <p className="share-saved-sub">
              Copy a link to update the Copies count.
            </p>
          </div>
          <span className="share-saved-count">{links.length} link(s)</span>
        </div>
        {loading ? (
          <Loader message="Loading links..." />
        ) : links.length === 0 ? (
          <NoData
            message="No share links yet"
            subText="Generate & copy a link to create the first record."
          />
        ) : (
          <div className="share-table-wrap">
            <table className="share-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Owner</th>
                  <th>URL</th>
                  <th>Code</th>
                  <th>Registers</th>
                  <th>Copies</th>
                  <th>Expires</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {links.map((row, idx) => {
                  const statusKey = !row.is_active
                    ? "off"
                    : row.is_expired
                      ? "expired"
                      : "on";
                  const statusLabel =
                    statusKey === "off"
                      ? "Off"
                      : statusKey === "expired"
                        ? "Expired"
                        : "Active";
                  const ownerName =
                    user?.role_id === 1 || (user?.role_id === 2 || user?.role_id === 5)
                      ? row.created_by_name || "—"
                      : "My link";

                  return (
                    <tr
                      key={row.id}
                      className={statusKey !== "on" ? "share-row-muted" : ""}
                    >
                      <td>{idx + 1}</td>
                      <td>
                        <div className="share-owner-cell">
                          <strong>{ownerName}</strong>
                          {(user?.role_id === 1 || (user?.role_id === 2 || user?.role_id === 5)) &&
                          row.created_by_role ? (
                            <small>{row.created_by_role}</small>
                          ) : null}
                        </div>
                      </td>
                      <td>
                        <div className="share-url-actions">
                          <button
                            type="button"
                            className="share-inline-copy"
                            onClick={() => handleCopySavedLink(row)}
                            title="Copy link"
                          >
                            <MdContentCopy size={14} /> Copy
                          </button>
                          <button
                            type="button"
                            className="share-url-text"
                            title={row.url}
                            onClick={() => handleCopySavedLink(row)}
                          >
                            {shortUrl(row.url)}
                          </button>
                        </div>
                      </td>
                      <td>
                        <span className="share-code">
                          {row.short_code || "—"}
                        </span>
                      </td>
                      <td className="share-num">{row.register_count || 0}</td>
                      <td className="share-num share-copies">
                        {row.copy_count || 0}
                      </td>
                      <td className="share-expires-cell">
                        {formatDate(row.expires_at)}
                      </td>
                      <td>
                        <span className={`share-badge ${statusKey}`}>
                          {statusLabel}
                        </span>
                      </td>
                      <td>
                        {row.is_active && !row.is_expired ? (
                          <button
                            type="button"
                            className="share-deactivate-btn"
                            onClick={() => handleDeactivate(row.id)}
                          >
                            Deactivate
                          </button>
                        ) : (
                          <span className="share-saved-muted">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShareLinkManage;
